/**
 * Which data a Card Management tab is rendering (EX-40600).
 *
 * Shared by the Activity and Statements seams so the two tabs on the same page
 * cannot drift apart on the one judgement that matters most here: whether the
 * user is looking at a real read, a fabricated stand-in, or a failure.
 *
 * The permissive direction is the dangerous one. Presenting demo rows as live
 * puts invented balances and invented audit history on screen with nothing
 * marking them — so anything not positively known to be real resolves to
 * `demo` (badged), `absent` (dropped) or `error` (surfaced), never silently to
 * `live`.
 *
 * `absent` vs `demo` is the distinction EX-40857 introduced. A card the server
 * has NOTHING for is not the same as a card the server will not answer for:
 *
 * - `demo`  — the card or org is not demo-enrolled (`403 CARD-MGMT.3`) or the
 *   route is not mounted (`404`). We cannot know what this card holds, so the
 *   fabricated surface stands in, badged.
 * - `absent` — an authorized, successful, EMPTY read. This IS the answer. The
 *   initial FIS bank-network integration does not guarantee a card has a next
 *   payment, payment history, or statements, so emptiness is a normal data
 *   state and the caller must DROP the affected surface rather than render a
 *   zero, an "N/A", a placeholder, or a figure inferred from balance or
 *   statement context.
 *
 * Callers must branch on `absent` explicitly. `CardOverviewStats` reached the
 * same shape from the other direction when EX-40596 deleted its `demo` member
 * in favour of `unavailable`.
 */

export type CardManagementSource =
  | 'loading'
  | 'live'
  | 'demo'
  | 'absent'
  | 'error'

/**
 * HTTP statuses that mean "this card has nothing to show", as distinct from
 * "the read failed".
 *
 * `403 CARD-MGMT.3` is the card or organization not being demo-enrolled;
 * `404` is either an unroutable path (these routes are non-production mocks
 * registered only for certain Spring profiles) or a card the actor cannot see.
 * None is a fault the user can act on or that retrying fixes — each is the
 * server saying there is nothing here — so they resolve to the demo surface
 * rather than to an error panel.
 *
 * Everything else (`4xx` we did not name, `5xx`, network failures, a malformed
 * response) stays an error. A `503 CARD-MGMT.15` is a transient outage, and
 * quietly substituting fabricated figures for it would misreport the card as
 * known-and-empty.
 */
const UNPROVISIONED_STATUSES = new Set([403, 404])

export const isUnprovisioned = (error: unknown): boolean =>
  error !== null &&
  typeof error === 'object' &&
  'status' in error &&
  typeof error.status === 'number' &&
  UNPROVISIONED_STATUSES.has(error.status)

/**
 * @param isLoading - True while the first read is in flight. Resolves to
 *   `loading` ahead of every other branch, so a tab never paints demo rows it
 *   is about to replace — the flash that made fabricated figures look real for
 *   a frame.
 * @param isError - Whether the read failed.
 * @param error - The failure, classified by `isUnprovisioned`.
 * @param hasData - Whether the successful read actually returned anything. A
 *   `200` with nothing in it is the contract's documented answer for a card
 *   with no history, and resolves to `absent` — the caller drops the surface.
 */
export const resolveCardManagementSource = ({
  isLoading,
  isError,
  error,
  hasData,
}: {
  isLoading: boolean
  isError: boolean
  error: unknown
  hasData: boolean
}): CardManagementSource => {
  if (isLoading) {
    return 'loading'
  }
  if (isError) {
    return isUnprovisioned(error) ? 'demo' : 'error'
  }
  // EX-40857: an empty success is a settled answer about the card, not a
  // stand-in for one. This branch returned `demo` until the FIS design handoff
  // made emptiness the expected state rather than the exceptional one, at which
  // point fabricated figures over a genuinely empty card became the bug.
  return hasData ? 'live' : 'absent'
}

/**
 * THE Statements-tab rule (EX-40857).
 *
 * The tab carries both financial tiles and survives on EITHER of them. It is
 * hidden from the primary tab navigation only when the Payments tile and the
 * Statements tile are BOTH absent — the fully empty two-tile case.
 *
 * The design handoff states this over three axes (next payment, payment
 * history, statements). Two are enough here because `paymentsState` already
 * folds the first two: the Payments tile resolves to `absent` only when the
 * card has neither a next payment nor any history, which is exactly
 * `!(nextPayment || payments)`.
 *
 * Anything not yet settled — `loading`, and the `demo` and `error` surfaces
 * which both render content — counts as present. That direction is deliberate:
 * a tab that exists and then vanishes mid-read is worse than one that briefly
 * shows a loading state, and a user who reaches a tab in that window is caught
 * by the deep-link guard rather than left on a dead end.
 */
export const hasAnyFinancialRecords = (
  paymentsState: CardManagementSource,
  statementsState: CardManagementSource,
): boolean => !(paymentsState === 'absent' && statementsState === 'absent')
